/**
 * Formes du payload du manuel utilisateur, produit par `npm run docs:build:app`
 * et servi en fichiers statiques depuis `assets/docs/`.
 *
 * Ce sont des interfaces, et non des modèles `ModelBase` : rien n'est hydraté
 * depuis l'API, rien ne se sauvegarde, rien ne passe par `GeneralFactory`.
 * Contrat de la source : `docs/user-manual/IN_APP.md` (fait foi).
 */

/** Nature d'un article, telle que la déclare son frontmatter. */
export type DocArticleType = 'guide' | 'comprendre' | 'reference' | 'depannage';

/** Le mot qui nomme la nature d'un article, tel qu'il s'affiche au lecteur. */
export const DOC_TYPE_LABELS: Record<DocArticleType, string> = {
    guide: 'Guide',
    comprendre: 'Comprendre',
    reference: 'Référence',
    depannage: 'Dépannage',
};

/**
 * Couleur du liseré d'un article, par nature de contenu. Table unique : le même
 * code couleur doit se lire à l'identique dans le panneau « ? » d'un écran et
 * dans les résultats de recherche du manuel.
 */
export const DOC_TYPE_BORDER_CLASSES: Record<DocArticleType, string> = {
    guide: 'border-blue-500',
    comprendre: 'border-green-500',
    reference: 'border-gray-400',
    depannage: 'border-yellow-500',
};

/** Intertitre ancré d'un article — alimente la recherche. */
export interface DocHeading {
    level: number;
    id: string;
    text: string;
}

/** Sous-groupe de lecture déclaré par une section. */
export interface DocGroup {
    key: string;
    title: string;
    order: number;
}

export interface DocSection {
    key: string;
    title: string;
    order: number;
    icon?: string;
    groups: DocGroup[];
    /** Articles de la section, dans l'ordre de lecture. */
    articles: string[];
    /** Articles d'autres sections listés ici aussi (cross-listing). */
    cross: string[];
}

export interface DocArticle {
    id: string;
    slug: string;
    title: string;
    description?: string;
    /** La question du lecteur — reprise par le panneau « ? ». */
    question?: string;
    section: string;
    group?: string;
    order?: number;
    type: DocArticleType;
    /** Article d'ouverture de sa section. */
    index?: boolean;
    comptable: 'bloc' | 'none';
    /** Fonctionnalité que l'écran documenté exige — sert au bandeau d'abonnement, jamais à masquer. */
    feature?: string;
    status: 'draft' | 'in-review' | 'validated';
    mainScreen?: string;
    screens: string[];
    messages?: string[];
    /** Parcours de lecture choisi par le rédacteur. */
    readNext: string[];
    /** Articles proches, calculés au build. */
    related: string[];
    /** Durée de lecture en minutes, calculée au build (prose + forfait par maquette). */
    readingTime: number;
    headings: DocHeading[];
}

export interface DocIndex {
    generatedAt: string;
    country: string;
    sections: DocSection[];
    articles: DocArticle[];
    /** Table écran → articles : `{ 'banking.transactions-bancaires': ['banque.…'] }`. */
    screens: Record<string, string[]>;
}
